import { eventChannel, EventChannel } from "redux-saga";
import {
  call,
  put,
  take,
  takeLatest,
  fork,
  cancel,
  cancelled,
  select,
} from "redux-saga/effects";
import {
  tournamentsSet,
  tournamentsUpsert,
  tournamentsClear,
  tournamentsListRequest,
  tournamentsListRefreshRequest,
  Sng,
  tournamentsTableLeave,
  tournamentsTableEnter,
  tournamentsRegisterRequest,
  tournamentsRegisterSuccess,
  tournamentsRegisterFailure,
  tournamentsInviteOpen,
  tournamentsUnregisterSuccess,
  tournamentsUnregisterFailure,
  tournamentsUnregisterRequest,
  tournamentsActiveRequest,
  tournamentsActiveSuccess,
  tournamentsActiveFailure,
} from "./tournamentsActions";
import { getSocket } from "../../services/socket";
import { navigateToJoinTournaments } from "../../utils/helpers/navigationService";
import Toast from "react-native-toast-message";

const tableFocus: Record<string, number> = {};

const extract = (packet: any) => {
  const jsonStr = packet?.r?.data ?? packet?.data ?? packet;
  try {
    return typeof jsonStr === "string" ? JSON.parse(jsonStr) : (jsonStr ?? {});
  } catch {
    return {};
  }
};

const withRegistered = (t: any, userIdStr: string): Sng => ({
  ...t,
  registered: Array.isArray(t?.users)
    ? t.users.map(String).includes(userIdStr)
    : false,
});

export const tournamentsSubscribe = { type: "tournaments/subscribe" } as const;
export const tournamentsUnsubscribe = {
  type: "tournaments/unsubscribe",
} as const;

export type TournamentsSubscribeAction = typeof tournamentsSubscribe;
export type TournamentsUnsubscribeAction = typeof tournamentsUnsubscribe;

function tournamentsChannel(socket: any, userIdStr: string): EventChannel<any> {
  return eventChannel((emit) => {
    const onAny = (_event: string, _payload?: any) => {};
    socket.onAny?.(onAny);

    const onSnGList = (packet: any) => {
      const obj = extract(packet);

      if (obj?.success === false) {
        console.log("getSnGTournaments error:", obj?.error, obj);
        emit({ type: "LIST", payload: [] });
        return;
      }

      const data = obj?.data ?? obj;

      const arr = Array.isArray(data)
        ? data
        : (Object.values(data || {}) as any[]);

      const mapped = arr.map((t) => withRegistered(t, userIdStr));

      emit({ type: "LIST", payload: mapped });
    };

    const onLobbyUpdate = (packet: any) => {
      const obj = extract(packet);
      if (obj?.success === false) return;

      const data = obj?.data ?? obj;
      emit({ type: "UPSERT_ONE", payload: withRegistered(data, userIdStr) });
    };

    const onInviteSnGOpen = (packet: any) => {
      emit({ type: "INVITE_OPEN", payload: packet });
    };

    const onTableUpdate = (packet: any) => {
      const obj = extract(packet);
      if (obj?.success === false) return;
      const data = obj?.data ?? obj;
      const tournamentId = data?.id ?? data?.tournamentId ?? data?.tournament_id;
      if (tournamentId) {
        socket.emit("tournament:getSnGTournaments", {});
        socket.emit("tournament:getSnGTournamentDetails", {
          tournament_id: tournamentId,
          room: tournamentId,
        });
      }
    };

    socket.off("getSnGTournaments", onSnGList);
    socket.off("getSnGLobbyUpdate", onLobbyUpdate);
    socket.off("inviteSnGTournamentOpen", onInviteSnGOpen);
    socket.on("getSnGTournaments", onSnGList);
    socket.on("getSnGLobbyUpdate", onLobbyUpdate);
    socket.on("inviteSnGTournamentOpen", onInviteSnGOpen);
    ["getSnGTableUpdateAdd", "tournament:getSnGTableUpdateAdd", "getSnGTableUpdateRemove", "tournament:getSnGTableUpdateRemove"].forEach(
      (e) => {
        socket.off(e, onTableUpdate);
        socket.on(e, onTableUpdate);
      },
    );

    return () => {
      socket.offAny?.(onAny);
      socket.off("getSnGTournaments", onSnGList);
      socket.off("getSnGLobbyUpdate", onLobbyUpdate);
      socket.off("inviteSnGTournamentOpen", onInviteSnGOpen);
      ["getSnGTableUpdateAdd", "tournament:getSnGTableUpdateAdd", "getSnGTableUpdateRemove", "tournament:getSnGTableUpdateRemove"].forEach(
        (e) => socket.off(e, onTableUpdate),
      );
    };
  });
}

function* waitForConnect(socket: any) {
  if (socket.connected) return;

  const chan: EventChannel<any> = eventChannel((emit) => {
    const h = () => emit(true);
    socket.once("connect", h);
    return () => socket.off("connect", h);
  });

  try {
    yield take(chan);
  } finally {
    chan.close();
  }
}

function* subscribeWorker() {
  const auth = yield select((s: any) => s.auth);
  const playerId = auth?.user?.id || "anonymous";
  const username = auth?.user?.username || "anonymous";

  const userUuidStr: string = String(auth?.user?.id ?? "");

  const socket = getSocket(playerId, username);

  yield call(waitForConnect, socket);

  const chan: EventChannel<any> = yield call(
    tournamentsChannel,
    socket,
    userUuidStr,
  );

  yield put(tournamentsListRequest());
  socket.emit("tournament:getSnGTournaments", {});
  socket.emit("service:enterSnGTournamentLobby", {});
  socket.emit("service:checkSvaraUsers", {});

  try {
    while (true) {
      const evt: {
        type: "LIST" | "UPSERT_ONE" | "INVITE_OPEN";
        payload: any;
      } = yield take(chan);

      if (evt.type === "LIST") {
        yield put(tournamentsSet(evt.payload));
      } else if (evt.type === "UPSERT_ONE") {
        yield put(tournamentsUpsert(evt.payload));
      } else if (evt.type === "INVITE_OPEN") {
        const packet = evt.payload;
        const { success, data } = packet || {};

        if (success && data) {
          const tournamentId = String(data.tournamentId);
          const entryLevel = Number(data.entry_level);

          yield call(navigateToJoinTournaments, tournamentId, entryLevel);

          // Optional alternative:
          // yield put(
          //   tournamentsInviteOpen({
          //     tournamentId,
          //     entryLevel,
          //   })
          // );
        }
      }
    }
  } finally {
    if (yield cancelled()) {
      chan.close();
      yield put(tournamentsClear());
      socket.emit?.("service:leaveSnGTournamentLobby", {});
    }
  }
}

function* refreshListWorker() {
  const auth = yield select((s: any) => s.auth);
  const playerId = auth?.user?.id || "anonymous";
  const username = auth?.user?.username || "anonymous";
  const socket = getSocket(playerId, username);
  socket.emit("tournament:getSnGTournaments", {});
}

function* emitTableEnter(action: ReturnType<typeof tournamentsTableEnter>) {
  const tableId = String(action.payload.table);
  tableFocus[tableId] = (tableFocus[tableId] ?? 0) + 1;

  if (tableFocus[tableId] !== 1) return;

  const auth = yield select((s: any) => s.auth);
  const playerId = auth?.user?.id || "anonymous";
  const username = auth?.user?.username || "anonymous";

  const socket = getSocket(playerId, username);
  yield call(waitForConnect, socket);

  socket.emit("service:enterSnGTournamentTable", { table: tableId });
}

function* emitTableLeave(action: ReturnType<typeof tournamentsTableLeave>) {
  const tableId = String(action.payload.table);
  const next = Math.max(0, (tableFocus[tableId] ?? 0) - 1);
  tableFocus[tableId] = next;

  if (next !== 0) return;

  const auth = yield select((s: any) => s.auth);
  const playerId = auth?.user?.id || "anonymous";
  const username = auth?.user?.username || "anonymous";

  const socket = getSocket(playerId, username);
  socket.emit("service:leaveSnGTournamentTable", { table: tableId });

  if (tableFocus[tableId] === 0) delete tableFocus[tableId];
}

function registerChannel(socket: any, userIdStr: string, tournamentId: string) {
  return eventChannel((emit) => {
    const handler = (...args: any[]) => {
      const rawPacket = normalizeSocketPacket(...args);
      const packet = extractPacketData(rawPacket);

      if (packet?.success === false) {
        emit(packet);
        return;
      }

      const data = packet?.data ?? packet ?? {};
      const user = packet?.user ?? data?.user ?? {};

      const packetUserId = String(user?.id ?? user?.numericId ?? "");
      const packetTournamentId = String(
        data?.id ?? data?.tournamentId ?? data?.tournament_id ?? "",
      );

      const userOk = !packetUserId || packetUserId === userIdStr;
      const tourOk = !packetTournamentId || packetTournamentId === tournamentId;

      if (userOk && tourOk) emit(packet);
    };

    const events = [
      "getSnGTableUpdateAdd",
      "tournament:getSnGTableUpdateAdd",
      "getSnGTableUpdateRemove",
      "tournament:getSnGTableUpdateRemove",
    ];

    events.forEach((e) => {
      socket.off(e, handler);
      socket.on(e, handler);
    });

    return () => {
      events.forEach((e) => socket.off(e, handler));
    };
  });
}

function* registerWorker(
  action: ReturnType<typeof tournamentsRegisterRequest>,
) {
  const { tournamentId } = action.payload;

  const auth = yield select((s: any) => s.auth);
  const playerId = auth?.user?.id || "anonymous";
  const username = auth?.user?.username || "anonymous";
  const userIdStr: string = String(
    auth?.user?.numericId ?? auth?.user?.id ?? "",
  );

  const socket = getSocket(playerId, username);
  yield call(waitForConnect, socket);

  const chan: EventChannel<any> = yield call(
    registerChannel,
    socket,
    userIdStr,
    tournamentId,
  );

  socket.emit("tournament:snGRegisterPlayer", {
    entity_id: tournamentId,
  });

  try {
    const packet = yield take(chan);

    if (packet?.success === false) {
      const errorMessage = packet?.error || "Failed to register for tournament";

      yield put(tournamentsRegisterFailure(errorMessage));

      Toast.show({
        type: "error",
        text1: "Registration Failed",
        text2: errorMessage,
        position: "bottom",
        visibilityTime: 3000,
        props: {
          height: 300,
          fontSize: 12,
        },
        text1Style: {
          fontSize: 12,
        },
        text2Style: {
          fontSize: 10,
          color: "#100f14ff",
        },
      });

      return;
    }

    yield put(
      tournamentsRegisterSuccess({
        tournamentId,
        packet,
      }),
    );

    socket.emit("tournament:getSnGTournamentDetails", {
      tournament_id: tournamentId,
      room: tournamentId,
    });
  } finally {
    chan.close();
  }
}

function* unregisterWorker(
  action: ReturnType<typeof tournamentsUnregisterRequest>,
) {
  const { tournamentId } = action.payload;

  const auth = yield select((s: any) => s.auth);
  const playerId = auth?.user?.id || "anonymous";
  const username = auth?.user?.username || "anonymous";
  const userIdStr: string = String(
    auth?.user?.numericId ?? auth?.user?.id ?? "",
  );

  const socket = getSocket(playerId, username);
  yield call(waitForConnect, socket);

  const chan: EventChannel<any> = yield call(
    registerChannel,
    socket,
    userIdStr,
    tournamentId,
  );

  socket.emit("tournament:snGUnregisterPlayer", {
    entity_id: tournamentId,
  });

  try {
    const packet = yield take(chan);

    if (packet?.success === false) {
      yield put(
        tournamentsUnregisterFailure(packet?.error || "Unregister failed"),
      );

      Toast.show({
        type: "error",
        text1: packet?.error || "Unregister failed",
        position: "bottom",
        visibilityTime: 2000,
      });

      return;
    }

    yield put(
      tournamentsUnregisterSuccess({
        tournamentId,
        packet,
      }),
    );
  } finally {
    chan.close();
  }
}

const normalizeSocketPacket = (...args: any[]) => {
  if (!args || args.length === 0) return undefined;

  if (typeof args[0] === "string" && args.length >= 2) {
    return args[1];
  }

  const p = args[0];

  if (Array.isArray(p)) {
    const lastObj = [...p].reverse().find((x) => x && typeof x === "object");
    return lastObj ?? p[0];
  }

  return p;
};

const extractPacketData = (packet: any) => {
  const jsonStr = packet?.r?.data ?? packet?.data ?? packet;
  try {
    return typeof jsonStr === "string" ? JSON.parse(jsonStr) : (jsonStr ?? {});
  } catch {
    return jsonStr ?? {};
  }
};

const normalizeActiveTournament = (item: any, roomId: string) => {
  const numberOfSeats = Number(item?.players ?? 6);
  const playersLeft = Number(item?.playersLeft ?? 0);
  const busySeats = Math.max(0, numberOfSeats - playersLeft);

  const seats = Array.from({ length: numberOfSeats }, (_, index) => ({
    seatNumber: index + 1,
    status: index < busySeats ? "BUSY" : "EMPTY",
  }));

  return {
    id: String(item?.id ?? item?.table_id ?? item?.tournamentId ?? ""),
    name: String(item?.id ?? item?.table_id ?? item?.tournamentId ?? "1"),
    minimal_chips: 1000,
    number_of_seats: numberOfSeats,
    seats,
    createdAt:
      item?.createdAt instanceof Date
        ? item.createdAt.toISOString()
        : String(item?.createdAt ?? ""),
    roomId: String(item?.roomId ?? item?.room_id ?? item?.room ?? roomId),
  };
};

function activeTournamentsChannel(socket: any, roomId: string) {
  return eventChannel((emit) => {
    const handler = (...args: any[]) => {
      const raw = normalizeSocketPacket(...args);
      const packet = extractPacketData(raw);

      if (packet?.success === false) {
        emit(packet);
        return;
      }

      const payload = packet?.data ?? packet ?? [];
      const arr = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : [];

      const mapped = arr.map((item: any) =>
        normalizeActiveTournament(item, roomId),
      );

      const filtered = mapped.filter((item: any) => {
        const packetRoomId = String(item?.roomId ?? "");
        return !packetRoomId || packetRoomId === String(roomId);
      });

      emit({
        success: true,
        data: filtered.length > 0 ? filtered : mapped,
      });
    };

    const events = [
      "tournament:getSnGActiveTournaments",
      "getSnGActiveTournaments",
    ];

    events.forEach((eventName) => {
      socket.off(eventName, handler);
      socket.on(eventName, handler);
    });

    return () => {
      events.forEach((eventName) => {
        socket.off(eventName, handler);
      });
    };
  });
}

function* fetchActiveTournamentsWorker(
  action: ReturnType<typeof tournamentsActiveRequest>,
) {
  const { roomId } = action.payload;

  const auth = yield select((s: any) => s.auth);
  const playerId = auth?.user?.id || "anonymous";
  const username = auth?.user?.username || "anonymous";

  const socket = getSocket(playerId, username);
  yield call(waitForConnect, socket);

  const chan: EventChannel<any> = yield call(
    activeTournamentsChannel,
    socket,
    roomId,
  );

  socket.emit("tournament:getSnGActiveTournaments", { roomId });

  try {
    const packet = yield take(chan);

    if (packet?.success === false) {
      const errorMessage = packet?.error || "Failed to load active tournaments";

      yield put(tournamentsActiveFailure({ roomId, error: errorMessage }));
      return;
    }

    let rawData = Array.isArray(packet?.data) ? packet.data : [];

    // if (!rawData.length) {
    //   rawData = [
    //     {
    //       id: `${roomId}-1`,
    //       players: 6,
    //       playersLeft: 4,
    //       createdAt: new Date().toISOString(),
    //     },
    //     {
    //       id: `${roomId}-2`,
    //       players: 8,
    //       playersLeft: 3,
    //       createdAt: new Date().toISOString(),
    //     },
    //     {
    //       id: `${roomId}-3`,
    //       players: 5,
    //       playersLeft: 2,
    //       createdAt: new Date().toISOString(),
    //     },
    //   ];
    // }

    const data = rawData.map((item: any) =>
      normalizeActiveTournament(item, roomId),
    );

    yield put(
      tournamentsActiveSuccess({
        roomId,
        data,
      }),
    );
  } catch (error: any) {
    yield put(
      tournamentsActiveFailure({
        roomId,
        error: error?.message || "Failed to load active tournaments",
      }),
    );
  } finally {
    chan.close();
  }
}

export function* tournamentsRootSaga() {
  let task: any = null;

  yield takeLatest("tournaments/subscribe", function* () {
    if (task) yield cancel(task);
    task = yield fork(subscribeWorker);
  });

  yield takeLatest("tournaments/unsubscribe", function* () {
    if (task) {
      yield cancel(task);
      task = null;
    }
  });

  yield takeLatest(tournamentsListRefreshRequest.type, refreshListWorker);
  yield takeLatest(tournamentsTableEnter.type, emitTableEnter);
  yield takeLatest(tournamentsTableLeave.type, emitTableLeave);
  yield takeLatest(tournamentsRegisterRequest.type, registerWorker);
  yield takeLatest(tournamentsUnregisterRequest.type, unregisterWorker);
  yield takeLatest(tournamentsActiveRequest.type, fetchActiveTournamentsWorker);
}
